import Link from 'next/link'
import styled from 'styled-components'
import { CDNImage } from '~/components/business/CDNImage'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useAuth } from '~/hooks/useAuth'
import { MessageEntry } from '~/components/pages/message/MessageEntry'
import { MyContainer } from '~/components/ui/MyContainer'
import { MyBtn } from '~/components/ui/MyBtn'
import { useStores } from '~/hooks/useStores'
import { observer } from 'mobx-react-lite'
import { Dropdown, Popover } from 'antd'
import { UserEntry } from '~/components/pages/user/UserEntry'
import { MenuOutlined } from '@ant-design/icons'
import { MyLink } from '~/components/ui/MyLink'
import { QRCodeCanvas } from 'qrcode.react'
import { MyIcon } from '~/components/ui/MyIcon'
import {
  HeaderNavItem,
  NavItemProps,
} from '~/components/business/HeaderNavItem'

import styles from './header.module.css'
import {
  Architecture,
  IconApi,
  IconCup,
  IconJiexi,
  IconNews,
  IconPlaza,
  Labor,
  Magic,
} from 'wujieai-react-icon'

export type HeaderType = 'no-margin' | 'normal'

const StyledHeader = styled.header<{
  type: HeaderType
  $dark: boolean
}>`
  background: ${props =>
    props.$dark ? props.theme['neutral-100'] : props.theme['neutral-900']};
  height: 56px;
  padding: 12px 0;
  z-index: 999;
  top: 0;
  position: sticky;
  ${props =>
    props.type === 'normal' &&
    `border-bottom: 1px solid ${
      props.$dark ? props.theme['neutral-300'] : props.theme['neutral-600']
    }`};
  ${props => props.type === 'normal' && 'margin-bottom: 24px'};
`

const navData: NavItemProps[] = [
  {
    name: 'AI创作',
    href: '/ai',
    exist: true,
    items: [
      {
        key: 'home',
        icon: <IconPlaza size={16} />,
        label: (
          <MyLink href="/" exist>
            词图
          </MyLink>
        ),
      },
      {
        key: 'competition',
        icon: <IconCup size={16} />,
        label: (
          <MyLink href="/ai-competition" exist>
            AI大赛
          </MyLink>
        ),
      },
      {
        key: 'tag',
        icon: <Magic size={16} />,
        label: (
          <MyLink href="/tag-generator" exist>
            咒语生成器
          </MyLink>
        ),
      },
      {
        key: 'img2tag',
        icon: <IconJiexi size={16} />,
        label: (
          <MyLink href="/lab/img2tag" exist>
            咒语解析器
          </MyLink>
        ),
      },
      {
        key: 'lab',
        icon: <Labor size={16} />,
        label: (
          <MyLink href="/lab" exist>
            AI实验室
          </MyLink>
        ),
      },
      {
        key: 'article',
        icon: <IconNews size={16} />,
        label: (
          <MyLink href="/articles" exist>
            新闻资讯
          </MyLink>
        ),
      },
    ],
  },
  {
    name: 'AI专业版',
    href: '/ai',
    exist: false,
    isNew: false,
    host: process.env.NEXT_PUBLIC_WUJIEAI_PRO_HOST,
    items: [
      {
        key: 'service',
        icon: <Architecture size={16} />,
        label: (
          <MyLink
            href="/enterprise-service"
            exist={false}
            host={process.env.NEXT_PUBLIC_WUJIEAI_PRO_HOST}
          >
            企业服务
          </MyLink>
        ),
      },
      {
        key: 'docs',
        icon: <IconApi size={16} />,
        label: (
          <a
            href="https://apifox.com/apidoc/shared-ecc069df-a9d5-4c86-b723-6dcd5cc79f81"
            target="_blank"
            rel="nofollow noopener noreferrer"
          >
            API 文档
          </a>
        ),
      },
    ],
  },
  {
    name: '无界魔镜',
    href: '/mirror',
    exist: false,
    isNew: true,
    host: process.env.NEXT_PUBLIC_HOST,
    items: [
      {
        key: 'docs',
        icon: <IconApi size={16} />,
        label: (
          <a
            href="https://apifox.com/apidoc/shared-3d887fd4-759a-4884-83b6-644b66640358"
            target="_blank"
            rel="nofollow noopener noreferrer"
          >
            API 文档
          </a>
        ),
      },
    ],
  },
  {
    name: 'DeepSeek',
    href: '/ai/deepseek',
    exist: true,
    isVip: true,
  },
  {
    name: '4o-image',
    href: '/ai/4o',
    exist: true,
    isVip: true,
  },
]

const links = [
  {
    label: (
      <a
        className="flex items-center text-xs py-1"
        href={process.env.NEXT_PUBLIC_WUJIEBANTU_HOST}
        target="_blank"
        rel="nofollow noopener noreferrer"
      >
        <CDNImage className="w-5 mr-2" src="/images/wujie@2x.png" /> 无界版图
      </a>
    ),
    key: 'wujiebantu',
  },
  {
    label: (
      <a
        className="flex items-center text-xs py-1"
        href={`${process.env.NEXT_PUBLIC_HOST}/mirror`}
        target="_blank"
        rel="nofollow noopener noreferrer"
      >
        <CDNImage className="w-5 mr-2" src="/images/wujie@2x.png" /> 无界魔镜
      </a>
    ),
    key: 'mirror',
  },
  {
    label: (
      <a
        className="flex items-center text-xs py-1"
        href={'https://www.matpool.com'}
        target="_blank"
        rel="nofollow noopener noreferrer"
      >
        <CDNImage
          className="w-[18px] h-5 mr-2 ml-1"
          src="/images/matpool-logo.png"
        />{' '}
        矩池云
      </a>
    ),
    key: 'matpool',
  },
]

export const Header: React.FC<{
  type: HeaderType
  className?: string
  dark?: boolean
  /** 显示header右侧模块 */
  headerShowRight?: boolean
}> = observer(({ type, className, dark = true, headerShowRight = true }) => {
  const router = useRouter()
  const { isLogin } = useAuth()
  const { store } = useStores()

  const onLogin = () => {
    store.modal?.setLoginVisible(true)
  }

  return (
    <StyledHeader type={type} className={className} $dark={dark}>
      <MyContainer className="flex justify-between items-center">
        <div className="flex items-center">
          <Link href="/" className="mr-6">
            <CDNImage
              alt="无界AI"
              src={dark ? '/images/logo-1.svg' : '/images/logo--black.svg'}
              className="h-8 w-[83px]"
            />
          </Link>

          <div
            className={classNames(styles.dropdownContainer, {
              [styles.dropdownLight]: !dark,
            })}
          >
            {navData.map(item => (
              <HeaderNavItem key={item.name} navItem={item} dark={dark} />
            ))}
          </div>
        </div>
        {headerShowRight && (
          <div className="flex items-center">
            <Popover
              overlayClassName={styles.popover}
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              content={
                <div className="bg-neutral-200 rounded-2xl text-center p-4">
                  <div className="w-[128px] h-[128px] bg-white p-1">
                    <QRCodeCanvas
                      value={'https://m.wujieai.com/download'}
                      size={120}
                    ></QRCodeCanvas>
                  </div>

                  <div className="text-neutral-500 mt-4 leading-none">
                    Android & iPhone
                  </div>
                </div>
              }
              placement="bottom"
              trigger={'hover'}
              zIndex={999}
            >
              <div className="relative w-6 h-6 flex items-center justify-center rounded-full border-neutral-300 border border-solid bg-primary cursor-pointer">
                <MyIcon
                  icon="mobile"
                  className="text-[12px] text-white block "
                />
              </div>
            </Popover>
            {isLogin ? (
              <>
                <MessageEntry />
                <UserEntry />
              </>
            ) : (
              <>
                <MyBtn
                  className="!h-8 !leading-4 !py-[6px] !ml-6"
                  $dark
                  onClick={onLogin}
                >
                  登录 / 注册
                </MyBtn>
              </>
            )}
            <Dropdown
              menu={{ items: links }}
              placement="bottomRight"
              trigger={['click']}
              overlayClassName="w-[120px] shodow-dm"
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
            >
              <MenuOutlined className="!text-neutral-400 hover:!text-primary cursor-pointer ml-6 transition-all" />
            </Dropdown>
          </div>
        )}
      </MyContainer>
    </StyledHeader>
  )
})
